// ============================================================================
// StartupChecklist (Option D PR-6) — the AM's go-live "make the start great"
// checklist, distinct from the admin 7-stage pipeline `checklist`. Stored on
// new_account_sop.startup_checklist as { item_key: {done, by, at} }, surfaced
// from the New Accounts card. Auto-archives to a read-only summary 7 days after
// the account is confirmed started; the data is kept (audit), the panel just
// collapses.
//
// Canonical steps mirror the real "New Account SOP" (Shared Drives › SOPs).
// Confirm wording with Ben against that doc if it changes.
// ============================================================================

import { useState } from 'react';
import { CheckCircle2, Circle, ClipboardList, ChevronDown, ChevronRight } from 'lucide-react';
import type { StartupChecklistItemState } from '../../shared/types';

interface TemplateItem {
  key: string;
  label: string;
}

export const STARTUP_CHECKLIST_TEMPLATE: TemplateItem[] = [
  { key: 'identify_sub', label: 'Identify the subcontractor' },
  { key: 'service_agreement', label: 'Sub & facility service agreement signed' },
  { key: 'supplies_ordered', label: 'Supplies / equipment ordered' },
  { key: 'fa_walkthrough', label: 'Final walkthrough with Facility Admin' },
  { key: 'access_coordinated', label: 'Access / keys / badges coordinated' },
  { key: 'dispensers_installed', label: 'Dispensers installed' },
  { key: 'sub_walkthrough', label: 'Walkthrough with the subcontractor' },
  { key: 'first_service', label: 'First-service confirmation' },
];

type ChecklistMap = Record<string, StartupChecklistItemState>;

export function StartupChecklist({
  value,
  onSave,
  currentEid,
  archived,
  disabled,
}: {
  value: ChecklistMap | null | undefined;
  onSave: (next: ChecklistMap) => Promise<void>;
  currentEid: string | null | undefined;
  /** Confirmed started + ≥7 days → read-only summary. */
  archived: boolean;
  disabled?: boolean;
}) {
  const map = value ?? {};
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!archived);

  const doneCount = STARTUP_CHECKLIST_TEMPLATE.filter((t) => map[t.key]?.done).length;
  const total = STARTUP_CHECKLIST_TEMPLATE.length;
  const pct = Math.round((doneCount / total) * 100);

  async function toggle(key: string) {
    if (disabled || archived) return;
    const cur = map[key]?.done ?? false;
    const next: ChecklistMap = {
      ...map,
      [key]: cur
        ? { done: false, by: null, at: null }
        : { done: true, by: currentEid ?? null, at: new Date().toISOString() },
    };
    setBusyKey(key);
    try {
      await onSave(next);
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-bg p-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 text-left"
      >
        <ClipboardList className="h-4 w-4 text-tcs-accent-ink" />
        <span className="text-sm font-semibold text-tcs-text">Go-live checklist</span>
        <span className="ml-1 rounded-full bg-tcs-surface px-2 py-0.5 text-[10px] font-semibold text-tcs-text-secondary">
          {doneCount}/{total}
        </span>
        {archived && (
          <span className="rounded-full bg-rag-green/10 px-2 py-0.5 text-[10px] font-semibold text-rag-green">
            Startup complete
          </span>
        )}
        {expanded ? (
          <ChevronDown className="ml-auto h-4 w-4 text-tcs-text-muted" />
        ) : (
          <ChevronRight className="ml-auto h-4 w-4 text-tcs-text-muted" />
        )}
      </button>

      {/* progress bar */}
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-tcs-surface">
        <div
          className={`h-full rounded-full ${pct === 100 ? 'bg-rag-green' : 'bg-tcs-accent'}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {expanded && (
        <ul className="mt-3 space-y-1">
          {STARTUP_CHECKLIST_TEMPLATE.map((t) => {
            const st = map[t.key];
            const done = st?.done ?? false;
            return (
              <li key={t.key}>
                <button
                  type="button"
                  disabled={disabled || archived || busyKey === t.key}
                  onClick={() => toggle(t.key)}
                  className={`flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                    archived || disabled ? 'cursor-default' : 'hover:bg-tcs-bg-muted'
                  }`}
                >
                  {done ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-rag-green" />
                  ) : (
                    <Circle className="mt-0.5 h-4 w-4 shrink-0 text-tcs-text-muted" />
                  )}
                  <span className="min-w-0">
                    <span className={`block ${done ? 'text-tcs-text-secondary line-through' : 'text-tcs-text'}`}>
                      {t.label}
                    </span>
                    {done && st?.at && (
                      <span className="block text-[10px] text-tcs-text-muted">
                        {st.by ? `${st.by} · ` : ''}
                        {new Date(st.at).toLocaleDateString()}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {archived && expanded && (
        <p className="mt-2 text-[11px] text-tcs-text-muted">
          Archived — the account went live over a week ago. Read-only.
        </p>
      )}
    </div>
  );
}

/** Confirmed started + ≥7 days ago → archive the checklist (read-only). */
export function isStartupArchived(startedConfirmedAt: string | null | undefined): boolean {
  if (!startedConfirmedAt) return false;
  const at = new Date(startedConfirmedAt).getTime();
  if (Number.isNaN(at)) return false;
  return Date.now() - at > 7 * 86_400_000;
}
